/**
 * Snapshot adapters transform flat ChipSnapshotData (collected at export time) into the
 * nested shapes that chip components expect — the same shapes normally provided by GQL
 * fragment reads. This lets chips render identically in PDF exports without a live GQL cache.
 *
 * Most contributors don't need to modify this file unless they're changing what data a
 * chip component reads during rendering. See useSnapshotResolution for the consumer side.
 */
import {
  BioentityAliasInfoNode,
  SequenceType,
  TagSchemaSequenceSchemaType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getPrefixToTypename} from 'graphql-client/APIIDPrefixToTypename';
import {maybeGetTypenameFromID} from 'graphql-client/getTypenameFromID';
import {SchemaValidationStatus} from 'schemas/SchemaValidationConstants';
import {apiIdToIconName} from 'util/APIUtil';

import {
  BioentityChipSnapshotData,
  ChipSnapshotData,
  EntryChipSnapshotData,
  StorableChipSnapshotData,
} from './SmartChipSnapshotContext';
import {SmartChipType} from './SmartChipTypes';

export type AdapterOutput =
  | StorableAdapterOutput
  | BioentityAdapterOutput
  | EntryAdapterOutput
  | GenericAdapterOutput;

export type SnapshotAdapter = (data: ChipSnapshotData) => AdapterOutput;

/**
 * Typed output for the storable adapter covering chip-rendering fields.
 * This does NOT include popover/tooltip-only fields (e.g. occupiedCapacity, createdAt, barcode).
 * See BNCH-153158 for extending typed adapters to other chip types.
 */
export interface StorableAdapterOutput {
  __typename: string;
  id: string;
  name: string | null | undefined;
  editUrl: string | null | undefined;
  url: string | null | undefined;
  archiveRecord: {purpose: string} | null;
  storableSchema: {iconId: string | null | undefined};
  checkOutStatusInfo: {status: string} | null;
  restrictionStatus: string | null | undefined;
  sampleRoles: {isCurrentUserRestrictedSampleUser: boolean} | null;
  expirationInfo: {isTrackingEnabled: boolean; isExpired: boolean};
  validationState: null;
}

export interface GenericAdapterOutput {
  __typename: string;
  id: string;
  name: string | null | undefined;
  editUrl: string | null | undefined;
  archiveRecord: {purpose: string} | null;
}

export interface EntryAdapterOutput extends GenericAdapterOutput {
  humanId: string | null | undefined;
}

export interface BioentityAdapterOutput {
  __typename: string;
  id: string;
  name: string | null | undefined;
  editUrl: string | null | undefined;
  archiveRecord: {purpose: string} | null;
  tagSchema: {
    iconId: string | null | undefined;
    includeRegistryIdInChips: boolean | null | undefined;
    sequenceType: TagSchemaSequenceSchemaType | null | undefined;
  } | null;
  sequenceType: SequenceType | null | undefined;
  schemaValidationStateJson: {status: SchemaValidationStatus} | null;
  bioentityAliasInfos: Array<BioentityAliasInfoNode>;
  secondaryText: string | null | undefined;
}

function baseFields(data: ChipSnapshotData): GenericAdapterOutput {
  const typename = data.id
    ? (maybeGetTypenameFromID(getPrefixToTypename(), data.id) ?? 'UnknownChipSummary')
    : 'UnknownChipSummary';
  return {
    __typename: typename,
    id: data.id,
    name: data.name,
    editUrl: data.url,
    archiveRecord: data.isArchived && data.archivePurpose != null ? {purpose: data.archivePurpose} : null,
  };
}

const genericAdapter = (data: ChipSnapshotData): GenericAdapterOutput => baseFields(data);

const entryAdapter = (data: EntryChipSnapshotData): EntryAdapterOutput => ({
  ...baseFields(data),
  humanId: data.humanId,
});

const storableAdapter = (data: StorableChipSnapshotData): StorableAdapterOutput => {
  const storable = data;
  return {
    ...baseFields(data),
    url: data.url,
    storableSchema: {
      iconId:
        storable.storableSchemaIconId ??
        (maybeGetTypenameFromID(getPrefixToTypename(), data.id) != null ? apiIdToIconName(data.id) : null),
    },
    checkOutStatusInfo: storable.checkOutStatus ? {status: storable.checkOutStatus} : null,
    restrictionStatus: storable.restrictionStatus,
    sampleRoles:
      storable.isCurrentUserAuthorized != null
        ? {isCurrentUserRestrictedSampleUser: storable.isCurrentUserAuthorized}
        : null,
    expirationInfo: {
      isTrackingEnabled: storable.isExpired != null,
      isExpired: storable.isExpired ?? false,
    },
    validationState: null,
  };
};

const bioentityAdapter = (data: BioentityChipSnapshotData): BioentityAdapterOutput => {
  const bioentity = data;
  return {
    ...baseFields(data),
    tagSchema:
      bioentity.tagSchemaIconId != null || bioentity.includeRegistryIdInChips != null
        ? {
            iconId: bioentity.tagSchemaIconId,
            includeRegistryIdInChips: bioentity.includeRegistryIdInChips,
            // tagSchema.sequenceType is not available in snapshot data (requires schema lookup)
            // and not needed for chip rendering
            sequenceType: undefined,
          }
        : null,
    sequenceType: bioentity.sequenceType,
    schemaValidationStateJson:
      bioentity.schemaValidationStatus != null ? {status: bioentity.schemaValidationStatus} : null,
    // Alias info is only used in alias management UI, not chip rendering
    bioentityAliasInfos: [],
    secondaryText: data.secondaryText ?? null,
  };
};

export const SNAPSHOT_ADAPTERS: Record<SmartChipType, SnapshotAdapter> = {
  ...(Object.fromEntries(Object.values(SmartChipType).map((type) => [type, genericAdapter])) as Record<
    SmartChipType,
    SnapshotAdapter
  >),
  [SmartChipType.ENTRY]: entryAdapter as SnapshotAdapter,
  [SmartChipType.STORABLE]: storableAdapter as SnapshotAdapter,
  [SmartChipType.BIOENTITY]: bioentityAdapter as SnapshotAdapter,
};

export function resolveFromSnapshot(
  id: string,
  snapshotMap: ReadonlyMap<string, ChipSnapshotData>,
  getSmartChipTypeFromId: (id: string) => SmartChipType
): {item: AdapterOutput | undefined; isInaccessible: boolean} | null {
  const entry = snapshotMap.get(id);
  if (!entry) {
    return null;
  }

  const chipType = getSmartChipTypeFromId(id);
  const adapter = SNAPSHOT_ADAPTERS[chipType];

  return {
    item: entry.isInaccessible ? undefined : adapter(entry),
    isInaccessible: entry.isInaccessible,
  };
}
